import { ACADEMIC_TRANSITION_TYPE } from '../constant/reEnrollment.constant';
import { STUDENT_STATUS } from './studentModel';

// Enum for student re-enrollment status
export enum STUDENT_RE_ENROLLMENT_STATUS {
  DRAFT = 'DRAFT', // Initial state
  PENDING = 'PENDING', // Awaiting admin decision
  PROMOTED = 'PROMOTED', // Moving to next grade
  DEMOTED = 'DEMOTED', // Moving to lower grade
  FAILED = 'FAILED', // Repeating same grade
  WITHDRAWN = 'WITHDRAWN', // Not returning
  GRADUATED = 'GRADUATED', // Completed final grade
  DROP_OFF = 'DROP_OFF', // Left school before completing final grade
}

// Single student in re-enrollment cycle
export interface IReEnrollmentStudent {
  id: string;
  studentId: string;
  studentFirstName: string;
  studentLastName: string;
  studentDisplayName: string;
  // Current lifecycle status from the Student record (enum, e.g. ACTIVE, GRADUATED)
  studentStatus?: STUDENT_STATUS;
  currentGrade: string;
  currentGradeId: string;
  currentClassroomName: string;
  currentClassroomId: string;
  assignedGrade: string;
  assignedGradeId: string;
  assignedClassroomName: string;
  assignedClassroomId: string;
  reEnrollmentStatus: STUDENT_RE_ENROLLMENT_STATUS;
  parentResponse: string | null;
  parentResponseDate: string | null;
  paymentStatus: string | null;
  adminNotes: string | null;
  profileImage: string | null;
}

// Pagination metadata for students
export interface IReEnrollmentStudentPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

// API response for fetching re-enrollment students list
export interface IReEnrollmentStudentListResponse {
  status: number;
  message: string;
  data: {
    students: IReEnrollmentStudent[];
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

// Query parameters for fetching re-enrollment students
export interface IReEnrollmentStudentQueryParams {
  page?: number;
  pageSize?: number;
  grade?: string;
  classroomId?: string;
  status?: STUDENT_RE_ENROLLMENT_STATUS;
  search?: string;
}

// POST: Initial Enrollment - Enroll unassigned students into classrooms
export interface IInitialEnrollmentPayload {
  students: string[];
  classroomId: string;
  notes?: string;
}

export interface IInitialEnrollmentSummaryData {
  successCount: number;
  errorCount: number;
  totalProcessed: number;
  cycleId: string;
  cycleStatus: string;
  enrolledStudents: Array<{
    studentId: string;
    studentName: string;
    toClassroom: string;
    status: string;
  }>;
  errors: Array<{
    studentId: string;
    studentName: string;
    error: string;
    errorCode: string;
  }>;
}

export interface IInitialEnrollmentResponse {
  status: number;
  message: string;
  data: IInitialEnrollmentSummaryData;
}

// Redux state interface for re-enrollment students
export interface IReEnrollmentStudentState {
  students: IReEnrollmentStudent[];
  pagination: IReEnrollmentStudentPagination;
  isLoading: boolean;
  isTransitioning: boolean;
  error: string | null;
  journeyState: IStudentJourneyState;
  enrollmentSummary?: IInitialEnrollmentSummaryData | null;
  crossCurriculumSummary?: ICrossCurriculumTransferSummaryData | null;
  // UNIFIED TRANSITION SUMMARY (CVA-3257)
  unifiedTransitionSummary?: IUnifiedTransitionSummaryData | null;
}

// Single journey log entry
export interface IStudentJourneyLog {
  id: string;
  transitionType: ACADEMIC_TRANSITION_TYPE;
  transitionDate: string;
  fromGrade: string;
  // Null for school-leaver transitions (GRADUATED / DROP_OFF)
  toGrade: string | null;
  fromClassroomName: string;
  // Null for school-leaver transitions (GRADUATED / DROP_OFF)
  toClassroomName: string | null;
  statusChange: string | null;
  reason: string;
  reEnrollmentCycleName: string | null;
  adminNotes: string;
  createdAt: string;
  updatedAt: string;
}

// API response for student journey logs
export interface IStudentJourneyResponse {
  status: number;
  message: string;
  data: {
    studentId: string;
    journey: IStudentJourneyLog[];
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

// Query parameters for fetching student journey
export interface IStudentJourneyQueryParams {
  page?: number;
  pageSize?: number;
}

// Redux state for student journey
export interface IStudentJourneyState {
  studentId: string | null;
  journey: IStudentJourneyLog[];
  pagination: IReEnrollmentStudentPagination;
  isLoading: boolean;
  error: string | null;
}



// Form interface for Initial Enrollment Modal with cascading dropdowns
export interface IInitialEnrollmentForm {
  curriculum: {
    value: string;
    isRequired: boolean;
    error: string | null;
    validator: string | null;
    disable: boolean;
  };
  grade: {
    value: string;
    isRequired: boolean;
    error: string | null;
    validator: string | null;
    disable: boolean;
  };
  classroom: {
    value: string;
    isRequired: boolean;
    error: string | null;
    validator: string | null;
    disable: boolean;
  };
  notes: {
    value: string;
    isRequired: boolean;
    error: string | null;
    validator: string | null;
    disable: boolean;
  };
}

// PATCH: Cross-Curriculum Transfer (Transfer students across different curricula)
export interface ICrossCurriculumTransferPayload {
  students: string[];
  currentClassroomId: string;
  newClassroomId: string;
  notes?: string;
}

export interface ICrossCurriculumTransferredStudent {
  studentId: string;
  studentName: string;
  fromClassroom: string;
  fromCurriculum: string;
  toClassroom: string;
  toCurriculum: string;
  status: string;
}

export interface ITransitionError {
  studentId: string;
  studentName: string;
  error: string;
  errorCode: string;
}

export interface ICrossCurriculumTransferSummaryData {
  successCount: number;
  errorCount: number;
  totalProcessed: number;
  cycleId: string;
  cycleStatus: string;
  transferredStudents: ICrossCurriculumTransferredStudent[];
  errors: ITransitionError[];
}

export interface ICrossCurriculumTransferResponse {
  status: number;
  message: string;
  data: ICrossCurriculumTransferSummaryData;
}

// UNIFIED TRANSITION TYPES (CVA-3257)

// Transition type enum matching backend
export enum TRANSITION_TYPE {
  PROMOTE = 'PROMOTE',
  DEMOTE = 'DEMOTE',
  TRANSFER = 'TRANSFER',
  GRADUATE = 'GRADUATE',
  DROP_OFF = 'DROP_OFF',
}

// Unified transition payload
export interface IUnifiedTransitionPayload {
  transitionType: TRANSITION_TYPE;
  students: string[];
  currentClassroomId: string;
  // Required for PROMOTE/DEMOTE/TRANSFER; MUST be omitted for GRADUATE/DROP_OFF
  newClassroomId?: string;
  isImmediate: boolean;
  notes?: string;
  reason?: string;
}

// Unified transition summary data
export interface IUnifiedTransitionSummaryData {
  successCount: number;
  errorCount: number;
  totalProcessed: number;
  cycleId: string;
  cycleStatus: string;
  transitionType: TRANSITION_TYPE;
  isImmediate: boolean;
  transitionedStudents: Array<{
    studentId: string;
    studentName: string;
    fromClassroom: string;
    // Null for school-leaver transitions (GRADUATE / DROP_OFF)
    toGrade?: string | null;
    toClassroom: string | null;
    status: string;
  }>;
  errors: Array<{
    studentId: string;
    studentName: string;
    error: string;
    errorCode: string;
  }>;
}

// Unified transition response
export interface IUnifiedTransitionResponse {
  status: number;
  message: string;
  data: IUnifiedTransitionSummaryData;
}

// Form interface for Cross-Curriculum Transfer Modal with cascading dropdowns
export interface ICrossCurriculumTransferForm {
  curriculum: {
    value: string;
    isRequired: boolean;
    error: string | null;
    validator: string | null;
    disable: boolean;
  };
  grade: {
    value: string;
    isRequired: boolean;
    error: string | null;
    validator: string | null;
    disable: boolean;
  };
  classroom: {
    value: string;
    isRequired: boolean;
    error: string | null;
    validator: string | null;
    disable: boolean;
  };
  notes: {
    value: string;
    isRequired: boolean;
    error: string | null;
    validator: string | null;
    disable: boolean;
  };
}
